import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ticketService, slaService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Send, Lock, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import './TicketDetail.css';

const STATUSES = ['Open', 'In Progress', 'Awaiting Client', 'Awaiting Internal', 'Resolved', 'Closed', 'Reopened'];

const STATUS_TRANSITIONS = {
  Open: ['In Progress', 'Awaiting Client', 'Awaiting Internal', 'Resolved', 'Closed'],
  'In Progress': ['Awaiting Client', 'Awaiting Internal', 'Resolved', 'Closed', 'Reopened'],
  'Awaiting Client': ['In Progress', 'Awaiting Internal', 'Resolved', 'Closed'],
  'Awaiting Internal': ['In Progress', 'Awaiting Client', 'Resolved', 'Closed'],
  Resolved: ['Reopened', 'Closed'],
  Closed: ['Reopened'],
  Reopened: ['In Progress', 'Awaiting Client', 'Awaiting Internal', 'Resolved', 'Closed'],
};

const RESOLUTION_CODES = [
  'Code Fix',
  'Configuration Change',
  'Data Correction',
  'Infrastructure Restart',
  'Dependency/Third-Party Issue',
  'User Action Required',
  'Duplicate Ticket',
  'No Repro / Won\'t Fix',
  'Other',
];

export default function TicketDetail() {
  const { id } = useParams();
  const { user, isSupport } = useAuth();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [comment, setComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [appLeadUserId, setAppLeadUserId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [resolutionCode, setResolutionCode] = useState('');
  const [resolutionSummary, setResolutionSummary] = useState('');
  const [reopenReason, setReopenReason] = useState('');
  const [slaPreview, setSlaPreview] = useState(null);
  const [savingWorkflow, setSavingWorkflow] = useState(false);

  const load = () => ticketService.getById(id).then((r) => setTicket(r.data.ticket));
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (!ticket?.app?._id) return;
    ticketService.getAssignees(ticket.app._id).then((r) => {
      setAssignees(r.data.users || []);
      setAppLeadUserId(r.data.appLeadUserId || null);
    });
  }, [ticket?.app?._id]);

  useEffect(() => {
    if (!ticket) return;
    setSelectedStatus(ticket.status || 'Open');
    setSelectedAssignee(ticket.assignedTo?._id || '');
    setResolutionCode(ticket.resolutionCode || '');
    setResolutionSummary(ticket.resolutionSummary || '');
    setReopenReason(ticket.reopenReason || '');
  }, [ticket]);

  useEffect(() => {
    if (!ticket?.client?._id || !ticket?.app?._id) return;
    slaService.preview({
      clientId: ticket.client._id,
      appId: ticket.app._id,
      priority: ticket.priority,
      category: ticket.category,
    }).then((res) => setSlaPreview(res.data.preview)).catch(() => setSlaPreview(null));
  }, [ticket?.client?._id, ticket?.app?._id, ticket?.priority, ticket?.category]);

  const handleSaveWorkflow = async () => {
    if (!ticket) return;
    const payload = {};
    if (selectedStatus && selectedStatus !== ticket.status) payload.status = selectedStatus;

    const currentAssigneeId = ticket.assignedTo?._id || '';
    if (selectedAssignee !== currentAssigneeId) {
      payload.assignedTo = selectedAssignee || null;
    }

    if (selectedStatus === 'Resolved') {
      payload.resolutionCode = resolutionCode;
      payload.resolutionSummary = resolutionSummary;
    }

    if (selectedStatus === 'Reopened') {
      payload.reopenReason = reopenReason;
    }

    if (Object.keys(payload).length === 0) return;

    setSavingWorkflow(true);
    try {
      await ticketService.update(id, payload);
      load();
    } finally {
      setSavingWorkflow(false);
    }
  };

  const handleAssignToMe = async () => {
    if (!user?._id) return;
    setSelectedAssignee(user._id);
    setSavingWorkflow(true);
    try {
      await ticketService.update(id, { assignedTo: user._id });
      load();
    } finally {
      setSavingWorkflow(false);
    }
  };

  const handleSubmitComment = async () => {
    if (!comment.trim()) return;
    setSubmitting(true);
    try {
      await ticketService.addComment(id, { body: comment, isInternal });
      setComment('');
      load();
    } finally { setSubmitting(false); }
  };

  if (!ticket) return <div className="loading">Loading ticket...</div>;

  const priorityCls = { Critical: 'badge-critical', High: 'badge-high', Medium: 'badge-medium', Low: 'badge-low' };
  const slaResponsePct = ticket.sla?.firstResponseAt
    ? 100
    : ticket.sla?.responseDueAt
      ? Math.min(100, ((Date.now() - new Date(ticket.createdAt)) / (new Date(ticket.sla.responseDueAt) - new Date(ticket.createdAt))) * 100)
      : 0;

  const allowedStatusChanges = STATUS_TRANSITIONS[ticket.status] || [];
  const statusOptions = [ticket.status, ...allowedStatusChanges.filter((s) => s !== ticket.status)].filter((s) => STATUSES.includes(s));

  return (
    <div className="ticket-detail">
      <button className="btn btn-ghost back-btn" onClick={() => navigate('/tickets')}>
        <ArrowLeft size={14} /> Back to Tickets
      </button>

      <div className="ticket-detail__layout">
        <div className="ticket-detail__main">
          <div className="card ticket-detail__header">
            <div className="ticket-detail__meta">
              <span className="ticket-num-lg">{ticket.ticketNumber}</span>
              <span className={`badge ${priorityCls[ticket.priority]}`}>{ticket.priority}</span>
              <span className="badge badge-open">{ticket.status}</span>
              <span className="badge" style={{ background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>{ticket.category}</span>
            </div>
            <h2 className="ticket-detail__title">{ticket.title}</h2>
            <p className="ticket-detail__desc">{ticket.description}</p>

            {ticket.widgetContext?.url && (
              <div className="context-chip">
                <ExternalLink size={12} />
                <span>{ticket.widgetContext.url}</span>
              </div>
            )}
          </div>

          <div className="comments-section">
            <h3 className="section-title">Activity</h3>
            {ticket.comments
              .filter((c) => !c.isInternal || isSupport)
              .map((c) => (
                <div key={c._id} className={`comment ${c.isInternal ? 'comment--internal' : ''} ${c.author?._id === user?._id ? 'comment--own' : ''}`}>
                  <div className="comment__avatar">{c.author?.name?.[0]?.toUpperCase()}</div>
                  <div className="comment__body">
                    <div className="comment__header">
                      <span className="comment__author">{c.author?.name}</span>
                      {c.isInternal && <span className="internal-tag"><Lock size={10} /> Internal</span>}
                      <span className="comment__time">{format(new Date(c.createdAt), 'MMM d, h:mm a')}</span>
                    </div>
                    <div className="comment__text">{c.body}</div>
                  </div>
                </div>
              ))}

            <div className="comment-composer card">
              {isSupport && (
                <div className="composer-tabs">
                  <button className={`composer-tab ${!isInternal ? 'active' : ''}`} onClick={() => setIsInternal(false)}>Reply to Client</button>
                  <button className={`composer-tab ${isInternal ? 'active' : ''}`} onClick={() => setIsInternal(true)}>
                    <Lock size={12} /> Internal Note
                  </button>
                </div>
              )}
              <textarea
                rows={4}
                placeholder={isInternal ? 'Internal note (not visible to client). Mention users with @email...' : 'Write a reply...'}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                style={isInternal ? { borderColor: 'var(--warning)', background: 'rgba(245,166,35,0.04)' } : {}}
              />
              <div className="composer-footer">
                <button className="btn btn-primary" onClick={handleSubmitComment} disabled={submitting || !comment.trim()}>
                  <Send size={13} /> {submitting ? 'Sending...' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="ticket-detail__sidebar">
          <div className="card sla-card">
            <h4 className="card-label">SLA Status</h4>
            <div className="sla-item">
              <span>First Response</span>
              {ticket.sla.firstResponseAt
                ? <span className="sla-ok">✓ Responded</span>
                : ticket.sla.responseBreached
                  ? <span className="sla-breached">⚠ Breached</span>
                  : <span className="sla-pending">{ticket.sla.responseDueAt ? format(new Date(ticket.sla.responseDueAt), 'MMM d, h:mm a') : '—'}</span>
              }
            </div>
            <div className="sla-bar">
              <div className="sla-bar__fill" style={{
                width: `${slaResponsePct}%`,
                background: slaResponsePct > 80 ? 'var(--danger)' : slaResponsePct > 50 ? 'var(--warning)' : 'var(--success)',
              }} />
            </div>
            <div className="sla-item">
              <span>Resolution Due</span>
              <span className={ticket.sla.resolutionBreached ? 'sla-breached' : 'sla-pending'}>
                {ticket.sla.resolutionDueAt ? format(new Date(ticket.sla.resolutionDueAt), 'MMM d') : '—'}
              </span>
            </div>
            {slaPreview && (
              <div className="sla-item">
                <span>Policy / Warning</span>
                <span className="sla-pending">{slaPreview.policyName} / {slaPreview.warningThresholdPct}%</span>
              </div>
            )}
          </div>

          {isSupport && (
            <div className="card">
              <h4 className="card-label">Workflow</h4>
              <div className="status-actions" style={{ display: 'grid', gap: 10 }}>
                <div>
                  <label className="form-label" style={{ marginBottom: 4, display: 'block' }}>Status</label>
                  <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                    {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div>
                  <label className="form-label" style={{ marginBottom: 4, display: 'block' }}>Assigned To</label>
                  <select value={selectedAssignee} onChange={(e) => setSelectedAssignee(e.target.value)}>
                    <option value="">Unassigned</option>
                    {assignees.map((a) => (
                      <option key={a._id} value={a._id}>
                        {a.name}{a._id === appLeadUserId ? ' (App Lead)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedStatus === 'Resolved' && (
                  <>
                    <div>
                      <label className="form-label" style={{ marginBottom: 4, display: 'block' }}>Resolution Code</label>
                      <select value={resolutionCode} onChange={(e) => setResolutionCode(e.target.value)}>
                        <option value="">Select code...</option>
                        {RESOLUTION_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="form-label" style={{ marginBottom: 4, display: 'block' }}>Resolution Summary</label>
                      <textarea rows={3} value={resolutionSummary} onChange={(e) => setResolutionSummary(e.target.value)} placeholder="Root cause and applied fix" />
                    </div>
                  </>
                )}

                {selectedStatus === 'Reopened' && (
                  <div>
                    <label className="form-label" style={{ marginBottom: 4, display: 'block' }}>Reopen Reason</label>
                    <textarea rows={3} value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} placeholder="Reason for reopening this ticket" />
                  </div>
                )}

                <button className="btn btn-ghost status-btn" onClick={handleAssignToMe} disabled={savingWorkflow || ticket.assignedTo?._id === user?._id}>
                  Assign to me
                </button>

                <button className="btn btn-primary status-btn" onClick={handleSaveWorkflow} disabled={savingWorkflow}>
                  {savingWorkflow ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}

          <div className="card detail-info">
            <h4 className="card-label">Details</h4>
            {[
              ['Client', ticket.client?.name],
              ['App', ticket.app?.name],
              ['Version', ticket.app?.version || '—'],
              ['Source', ticket.source],
              ['Resolution Code', ticket.resolutionCode || '—'],
              ['Reopen Reason', ticket.reopenReason || '—'],
              ['Assigned To', ticket.assignedTo?.name || 'Unassigned'],
              ['Reported By', ticket.reportedByEmail || ticket.reportedBy?.name],
              ['Created', format(new Date(ticket.createdAt), 'MMM d, yyyy')],
            ].map(([k, v]) => (
              <div key={k} className="detail-row">
                <span className="detail-key">{k}</span>
                <span className="detail-val">{v}</span>
              </div>
            ))}
          </div>

          {ticket.widgetContext && (
            <div className="card">
              <h4 className="card-label">Captured Context</h4>
              {Object.entries(ticket.widgetContext)
                .filter(([, v]) => v)
                .map(([k, v]) => (
                  <div key={k} className="detail-row">
                    <span className="detail-key">{k}</span>
                    <span className="detail-val context-val">{typeof v === 'object' ? JSON.stringify(v) : v}</span>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
